<template>
  <div class="status-breakdown-card">
    <div class="card-header">
      <h4>{{ $t('globals.order_status_breakdown') || 'Order Status Breakdown' }}</h4>
      <div class="refresh-button" @click="fetchData">
        <icon name="sync-alt" :spin="isLoading"></icon>
      </div>
    </div>

    <div class="card-content">
      <div v-if="isLoading" class="loading-state">
        <icon name="spinner" spin class="spinner"></icon>
        <p>Loading status breakdown...</p>
      </div>

      <div v-else-if="items.length === 0" class="empty-state">
        No data available for the selected date range
      </div>

      <div v-else class="items-list">
        <div v-for="item in items" :key="item.name" class="item-row">
          <div class="item-header">
            <h5 class="item-name">{{ formatItemName(item.name) }}</h5>
            <span class="item-total">Total: {{ item.total }}</span>
          </div>

          <div class="status-bars">
            <div class="status-bar-container">
              <div class="status-bar">
                <div
                  v-if="item.created > 0"
                  class="status-segment status-created"
                  :style="{ width: getPercentage(item.created, item.total) + '%' }"
                  v-b-tooltip.hover
                  :title="`Created: ${item.created} (${getPercentage(item.created, item.total)}%)`"
                >
                  <span v-if="getPercentage(item.created, item.total) > 10" class="segment-label">
                    {{ item.created }}
                  </span>
                </div>
                <div
                  v-if="item.confirmed > 0"
                  class="status-segment status-confirmed"
                  :style="{ width: getPercentage(item.confirmed, item.total) + '%' }"
                  v-b-tooltip.hover
                  :title="`Confirmed: ${item.confirmed} (${getPercentage(item.confirmed, item.total)}%)`"
                >
                  <span v-if="getPercentage(item.confirmed, item.total) > 10" class="segment-label">
                    {{ item.confirmed }}
                  </span>
                </div>
                <div
                  v-if="item.requested > 0"
                  class="status-segment status-requested"
                  :style="{ width: getPercentage(item.requested, item.total) + '%' }"
                  v-b-tooltip.hover
                  :title="`Requested: ${item.requested} (${getPercentage(item.requested, item.total)}%)`"
                >
                  <span v-if="getPercentage(item.requested, item.total) > 10" class="segment-label">
                    {{ item.requested }}
                  </span>
                </div>
                <div
                  v-if="item.fulfilled > 0"
                  class="status-segment status-fulfilled"
                  :style="{ width: getPercentage(item.fulfilled, item.total) + '%' }"
                  v-b-tooltip.hover
                  :title="`Fulfilled: ${item.fulfilled} (${getPercentage(item.fulfilled, item.total)}%)`"
                >
                  <span v-if="getPercentage(item.fulfilled, item.total) > 10" class="segment-label">
                    {{ item.fulfilled }}
                  </span>
                </div>
              </div>
            </div>

            <div class="status-legend">
              <div class="legend-item">
                <span class="legend-color status-created"></span>
                <span class="legend-text">Created: {{ item.created }}</span>
              </div>
              <div class="legend-item">
                <span class="legend-color status-confirmed"></span>
                <span class="legend-text">Confirmed: {{ item.confirmed }}</span>
              </div>
              <div class="legend-item">
                <span class="legend-color status-requested"></span>
                <span class="legend-text">Requested: {{ item.requested }}</span>
              </div>
              <div class="legend-item">
                <span class="legend-color status-fulfilled"></span>
                <span class="legend-text">Fulfilled: {{ item.fulfilled }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import "vue-awesome/icons/spinner";
import "vue-awesome/icons/sync-alt";

export default {
  components: {
    Icon: () => import("vue-awesome/components/Icon.vue")
  },
  props: {
    range: Array
  },
  data() {
    return {
      items: [],
      isLoading: false
    };
  },
  methods: {
    ...mapActions(["noSynatecOhraOrdersByStatus"]),
    fetchStatusBreakdown() {
      this.isLoading = true;

      this.noSynatecOhraOrdersByStatus({ range: this.range })
        .then(res => {
          if (res && res.data && res.data.data && res.data.data.noSynatecOrdersByStatus) {
            const data = JSON.parse(res.data.data.noSynatecOrdersByStatus);
            // Calculate totals for each item
            this.items = data.map(item => ({
              ...item,
              total: item.created + item.confirmed + item.requested + item.fulfilled
            }));
          } else {
            this.items = [];
          }
        })
        .catch(err => {
          console.error("Error fetching status breakdown:", err);
          this.items = [];
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    fetchData() {
      this.fetchStatusBreakdown();
    },
    getPercentage(value, total) {
      if (total === 0) return 0;
      return Math.round((value / total) * 100);
    },
    formatItemName(name) {
      const nameMap = {
        "ULU-COLLI-OHRA": "Boxes",
        "ULU-EXT-CABLE": "Cables",
        "ULU-GPSONLY-EXTCABLE": "GPS Only Cables",
        "ULU-VEL10-OHRA": "Dongles"
      };
      return nameMap[name] || name;
    }
  },
  computed: {
    ...mapGetters(["getUserSystem"])
  },
  watch: {
    getUserSystem() {
      this.fetchData();
    },
    range() {
      this.fetchData();
    }
  },
  mounted() {
    this.fetchData();
  }
};
</script>

<style scoped>
.status-breakdown-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  height: 100%;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #e2e8f0;
}

.card-header h4 {
  margin: 0;
  color: #2d3748;
  font-size: 1.1em;
  font-weight: 600;
}

.refresh-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background-color: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  color: #4a5568;
  transition: all 0.2s;
}

.refresh-button:hover {
  background-color: #edf2f7;
  border-color: #5469d4;
  color: #5469d4;
}

.card-content {
  min-height: 200px;
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #718096;
}

.loading-state .spinner {
  font-size: 32px;
  margin-bottom: 12px;
  color: #5469d4;
}

.empty-state {
  font-style: italic;
  color: #a0aec0;
}

.items-list {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.item-row {
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #5469d4;
}

.item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.item-name {
  margin: 0;
  font-size: 1em;
  font-weight: 600;
  color: #2d3748;
}

.item-total {
  font-size: 0.9em;
  color: #4a5568;
  font-weight: 500;
}

.status-bars {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.status-bar-container {
  width: 100%;
}

.status-bar {
  display: flex;
  height: 40px;
  width: 100%;
  border-radius: 6px;
  overflow: hidden;
  background-color: #e2e8f0;
}

.status-segment {
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
  position: relative;
  min-width: 2px;
}

.status-segment:hover {
  opacity: 0.8;
  cursor: pointer;
}

.segment-label {
  color: white;
  font-size: 0.85em;
  font-weight: 600;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.status-created {
  background-color: #fbbf24;
}

.status-confirmed {
  background-color: #60a5fa;
}

.status-requested {
  background-color: #a78bfa;
}

.status-fulfilled {
  background-color: #34d399;
}

.status-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding-top: 8px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85em;
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 3px;
}

.legend-text {
  color: #4a5568;
  font-weight: 500;
}

@media (max-width: 768px) {
  .status-breakdown-card {
    padding: 15px;
  }

  .item-row {
    padding: 12px;
  }

  .status-bar {
    height: 32px;
  }

  .status-legend {
    gap: 12px;
  }

  .legend-item {
    font-size: 0.8em;
  }
}
</style>
